import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, labelStyle } from '../theme';
import BeforeAfterCompare from './BeforeAfterCompare';

const CATEGORIES = [
  { key:'all',               label:'All' },
  { key:'clinical-photo',    label:'Clinical Photos' },
  { key:'x-ray',             label:'X-Rays' },
  { key:'consent',           label:'Consent Forms' },
  { key:'lab-report',        label:'Lab Reports' },
  { key:'referral',          label:'Referrals' },
  { key:'id-proof',          label:'ID Proofs' },
  { key:'insurance-card',    label:'Insurance Cards' },
  { key:'discharge-summary', label:'Discharge Summary' },
  { key:'other',             label:'Other' },
];

const DocumentVault = ({ patientId, API_BASE, UPLOAD_BASE, triggerUpload }) => {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  // Parent can auto-open the upload modal by incrementing `triggerUpload`
  // (e.g., when the patient profile FAB's "Upload doc" action fires).
  // Mirrors the triggerNewVisit pattern in PatientVisitView.
  useEffect(() => {
    if (triggerUpload) setShowUpload(true);
  }, [triggerUpload]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('other');
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showCompare, setShowCompare] = useState(false);

  const fetchDocs = async () => {
    try {
      const url = filter === 'all'
        ? `${API_BASE}/documents/patient/${patientId}`
        : `${API_BASE}/documents/patient/${patientId}?category=${filter}`;
      const r = await axios.get(url);
      setDocs(r.data);
    } catch(e) { setDocs([]); }
  };

  useEffect(() => { fetchDocs(); }, [patientId, filter]); // eslint-disable-line

  const handleUpload = async () => {
    if (!file) return alert('Select a file');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('patientId', patientId);
      formData.append('title', title || file.name);
      formData.append('category', category);
      formData.append('tags', JSON.stringify(tags.split(',').map(t=>t.trim()).filter(Boolean)));
      formData.append('notes', notes);
      await axios.post(`${API_BASE}/documents/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFile(null); setTitle(''); setCategory('other'); setTags(''); setNotes('');
      setShowUpload(false); fetchDocs();
    } catch(e) { alert('Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    await axios.delete(`${API_BASE}/documents/${id}`);
    fetchDocs();
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
  const fmtSize = b => {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024*1024) return `${(b/1024).toFixed(1)} KB`;
    return `${(b/(1024*1024)).toFixed(1)} MB`;
  };

  const isImage = (fileType) => fileType && fileType.startsWith('image/');

  return (
    <div>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px'}}>
        <h3 style={{margin:0, color:theme.navy}}>Document Vault</h3>
        <div style={{display:'flex', gap:'10px'}}>
          <button onClick={()=>{ setShowCompare(c=>!c); if(!showCompare) setShowUpload(false); }}
            style={{padding:'9px 20px', backgroundColor: showCompare ? theme.warning : '#6c757d', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'bold'}}>
            {showCompare ? '✕ Close Compare' : 'Before/After Compare'}
          </button>
          <button onClick={()=>{ setShowUpload(!showUpload); if(!showUpload) setShowCompare(false); }}
            style={{padding:'9px 20px', backgroundColor:theme.primary, color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'bold'}}>
            {showUpload ? '✕ Cancel' : '+ Upload Document'}
          </button>
        </div>
      </div>

      {/* Upload form */}
      {showUpload && (
        <div style={{...cardStyle, marginBottom:'18px', borderTop:`4px solid ${theme.primary}`, backgroundColor:'#f8fffe'}}>
          <h4 style={{margin:'0 0 14px', color:theme.primaryDark}}>Upload New Document</h4>
          <div style={{display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:'12px', marginBottom:'12px'}}>
            <div>
              <label style={labelStyle}>Title</label>
              <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Document title..." style={inputStyle}/>
            </div>
            <div>
              <label style={labelStyle}>Category</label>
              <select value={category} onChange={e=>setCategory(e.target.value)} style={inputStyle}>
                {CATEGORIES.filter(c=>c.key!=='all').map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>File *</label>
              <input type="file" accept="image/*,.pdf,.doc,.docx" onChange={e=>setFile(e.target.files[0])} style={{...inputStyle, padding:'7px'}}/>
            </div>
            <div>
              <label style={labelStyle}>Tags (comma separated)</label>
              <input value={tags} onChange={e=>setTags(e.target.value)} placeholder="before, after, tooth-14" style={inputStyle}/>
            </div>
            <div style={{gridColumn:'span 2'}}>
              <label style={labelStyle}>Notes</label>
              <input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Optional notes..." style={inputStyle}/>
            </div>
          </div>
          <button onClick={handleUpload} disabled={uploading}
            style={{padding:'10px 22px', backgroundColor:theme.success, color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600'}}>
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      )}

      {/* Before/After Compare view */}
      {showCompare && (
        <div style={{marginBottom:'18px'}}>
          <BeforeAfterCompare documents={docs} UPLOAD_BASE={UPLOAD_BASE} />
        </div>
      )}

      {/* Category filter pills */}
      <div style={{display:'flex', gap:'6px', marginBottom:'18px', flexWrap:'wrap'}}>
        {CATEGORIES.map(c => (
          <button key={c.key} onClick={()=>setFilter(c.key)}
            style={{padding:'5px 14px', borderRadius:'16px', border:'1px solid #dee2e6', cursor:'pointer', fontSize:'0.78rem', fontWeight:'600',
              backgroundColor: filter===c.key ? theme.primary : 'white', color: filter===c.key ? 'white' : '#555'}}>
            {c.label}
            {c.key !== 'all' && (() => {
              const count = docs.filter(d => c.key === 'all' || d.category === c.key).length;
              return count > 0 ? ` (${count})` : '';
            })()}
          </button>
        ))}
      </div>

      {/* Document grid */}
      {docs.length === 0 ? (
        <div style={{textAlign:'center', color:'#bbb', padding:'50px', backgroundColor:'#fafafa', borderRadius:'10px'}}>
          No documents in this category
        </div>
      ) : (
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'16px'}}>
          {docs.map(doc => (
            <div key={doc._id} style={{borderRadius:'12px', overflow:'hidden', border:`1px solid ${theme.border}`, backgroundColor:'white', boxShadow:theme.shadowSm}}>
              {/* Preview */}
              <div style={{height:'140px', backgroundColor:'#f8f9fa', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', overflow:'hidden'}}
                onClick={()=>window.open((doc.url || `${UPLOAD_BASE}/${doc.filePath}`), '_blank')}>
                {isImage(doc.fileType) ? (
                  <img src={(doc.url || `${UPLOAD_BASE}/${doc.filePath}`)} alt={doc.title}
                    style={{width:'100%', height:'100%', objectFit:'cover'}}
                    onError={e=>{ e.target.style.display='none'; e.target.parentNode.innerHTML='<div style="color:#aaa;font-size:2rem">📄</div>'; }}/>
                ) : (
                  <div style={{fontSize:'2.5rem', opacity:0.4}}>
                    {doc.fileType?.includes('pdf') ? '📕' : '📄'}
                  </div>
                )}
              </div>
              {/* Info */}
              <div style={{padding:'10px 12px'}}>
                <div style={{fontWeight:'600', color:theme.navy, fontSize:'0.85rem', marginBottom:'4px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                  {doc.title || 'Untitled'}
                </div>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px'}}>
                  <span style={{padding:'2px 8px', borderRadius:'10px', fontSize:'0.68rem', fontWeight:'600',
                    backgroundColor:theme.primaryLight, color:theme.primaryDark}}>
                    {CATEGORIES.find(c=>c.key===doc.category)?.label || doc.category}
                  </span>
                  <span style={{fontSize:'0.72rem', color:theme.textSubtle}}>{fmtSize(doc.fileSize)}</span>
                </div>
                <div style={{fontSize:'0.72rem', color:theme.textSubtle, marginBottom:'6px'}}>{fmtDate(doc.createdAt)}</div>
                {doc.tags?.length > 0 && (
                  <div style={{display:'flex', gap:'4px', flexWrap:'wrap', marginBottom:'6px'}}>
                    {doc.tags.map((tag, i) => (
                      <span key={i} style={{padding:'1px 6px', borderRadius:'8px', fontSize:'0.65rem', backgroundColor:'#f0f0f0', color:'#555'}}>{tag}</span>
                    ))}
                  </div>
                )}
                <button onClick={()=>handleDelete(doc._id)}
                  style={{padding:'3px 8px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'4px', cursor:'pointer', fontSize:'0.7rem', fontWeight:'600'}}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentVault;
